/** @jsxImportSource @emotion/react */
import { useDisclosure } from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  getEmailRestrictions,
  removeEmailRestrictions,
} from "../../../api/User";
import FaTrashAltIcon from "../../../assets/icon/fa-trash-alt";
import { UserAtom } from "../../../atoms/UserAtom";
import { UserEmailRestrictionsAtom } from "../../../atoms/UserEmailRestrictionsAtom";
import { IPartialKeyword } from "../../../generated/Api";
import countries from "../../../utils/countries.json";
import Warning from "../../Modal/container/Warning";
import { useModal } from "../../Modal/Modal";
import SearchInput from "../../SearchInput";
import { BlockingTypes } from "../Blocking";
import BlackWhiteListModal from "./Inputs/BlackWhiteListModal";
import BlockCountry from "./Inputs/BlockCountry";
import BlockCountryCode from "./Inputs/BlockCountryCode";
import BlockDeviceID from "./Inputs/BlockDeviceID";
import BlockDeviceIP from "./Inputs/BlockDeviceIP";
import BlockDomain from "./Inputs/BlockDomain";
import BlockEmail from "./Inputs/BlockEmail";
import BlockKeyword, { MATCH_MODE_LABEL_MAP } from "./Inputs/BlockKeyword";
import BlockPhone from "./Inputs/BlockPhone";

const TableContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;

  .label-text {
    font-size: 16px;
    font-weight: 600;
    color: #222222;
  }
`;

const TableHeader = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  .country-button {
    font-size: 15px;
    font-weight: 600;
    color: #fb972e;

    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .table-title {
    font-size: 18px;
    font-weight: 600;
    color: #222;
  }

  .add-input-wrapper {
    width: 100%;
    margin: 16px 0 32px;

    display: flex;
    align-items: center;
    justify-content: center;

    grid-gap: 16px;
  }

  .label-search-wrapper {
    width: 100%;

    display: flex;
    align-items: center;
    justify-content: space-between;

    margin-bottom: 24px;

    .input-wrapper {
      width: 288px;
    }
  }
`;

const TableCardHeader = styled.div<{ isKeyword?: boolean }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.isKeyword ? "1fr repeat(2, .5fr) 20px" : "1fr 20px"};
  align-items: center;

  padding: 15px 25px;

  height: 60px;
  border: 1px solid #eceeef;
  border-radius: 4px;
`;

const TableHeadText = styled.div`
  display: flex;
  align-items: center;

  color: #707070;
  font-size: 14px;
  font-weight: 600;

  cursor: default;
  position: relative;
`;

const TableActions = styled.div`
  color: #707070;
  display: flex;
  font-size: 14px;
  align-items: center;
  justify-content: flex-end;
  font-weight: 600;
  cursor: default;
`;

const Icon = styled.img`
  width: 10px;
  height: 10px;
  margin-left: 8px;

  cursor: pointer;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  :hover {
    opacity: 0.6;
  }
`;

const TableRowWrapper = styled.div`
  width: 100%;
  max-height: 435px;
  min-height: 100%;

  overflow-y: auto;

  margin-top: 10px;

  display: grid;
  grid-template-rows: repeat(1, 1fr);
  grid-gap: 10px;
  align-items: center;

  .image-wrapper {
    margin: 0 auto;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    margin-top: 64px;

    .primary {
      margin: 24px 0 8px;

      font-size: 20px;
      font-weight: 600;
      color: #222;
      text-align: center;
    }

    .secondary {
      font-size: 16px;
      font-weight: 400;
      color: #707070;
      text-align: center;
    }
  }
`;

const TableRowItem = styled.div<{ isKeyword?: boolean }>`
  width: 100%;
  height: 60px;

  display: grid;
  grid-template-columns: ${(props) =>
    props.isKeyword ? "1fr repeat(2, .5fr) 20px" : "1fr 20px"};
  align-items: center;
  justify-content: space-between;

  padding: 15px 25px;

  background-color: #fff;
  box-shadow: rgba(27, 31, 35, 0.04) 0px 1px 0px,
    rgba(255, 255, 255, 0.25) 0px 1px 0px inset;

  border: 1px solid #eaeaea;
  border-radius: 2px;

  .text {
    color: #2f2f2f;
    font-size: 16px;
  }

  .icon-button {
    margin-left: auto;

    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 20px;
      height: 20px;

      cursor: pointer;

      fill: #979797;

      transition: fill 0.3s ease-in-out;
    }

    &:hover {
      svg {
        fill: #dc143c;
      }
    }
  }
`;

const BlockTag = styled.div`
  height: 28px;
  padding: 4px 9px 2px 9px;

  width: fit-content;

  &.Block {
    width: fit-content;
    padding: 4px 8px;
    border-radius: 2px;
    background-color: #e8ecef;
    color: #688195;
  }

  &.Suspended {
    width: fit-content;
    padding: 4px 8px;
    border-radius: 2px;
    background-color: #f9e7e5;
    color: #c41700;
  }

  &.Flag {
    width: fit-content;
    padding: 4px 8px;
    border-radius: 2px;
    background-color: #fdf5e1;
    color: #e3a600e3;
  }
`;

const Flag = styled.img`
  //height: 20px;
  width: 20px;
`;

interface BlockingTableProps {
  selected?: BlockingTypes;
  values: string[];

  keyword?: {
    blocked: string[];
    flagged: string[];
    suspended: string[];

    partialKeywordsBlocked?: IPartialKeyword[];
    partialKeywordsFlagged?: IPartialKeyword[];
    partialKeywordsSuspended?: IPartialKeyword[];
  };

  // Used to disable the toggle between blacklist or whitelist when
  // one of the two are filled
  disableWhitelistBlacklistToggle?: boolean;

  // Needed for popups, modals, etc
  type: BlockingTypes;
}

enum SortbyName {
  Ascending = "asc",
  Descending = "desc",
}

export default function BlockingTable({
  selected,
  values,
  keyword,
  disableWhitelistBlacklistToggle,
  type,
}: BlockingTableProps) {
  const [filtered, setFiltered] = useState("");

  const modal = useModal();
  const [user, setUser] = useRecoilState(UserAtom);
  const setEmailRestrictions = useSetRecoilState(UserEmailRestrictionsAtom);
  const [sortByName, setSortByName] = useState<SortbyName>(
    SortbyName.Ascending
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [filteredValues, setFilteredValues] = useState([]);
  const [flag, setFlag] = useState(false);

  useEffect(() => {
    if (!values || !values.length) return;

    setFilteredValues(
      values
        .filter((v) => v.toLowerCase().includes(filtered.toLowerCase()))
        .sort((a, b) => a.localeCompare(b))
    );
  }, [values, filtered]);

  useEffect(() => {
    if (!filteredValues.length) return;

    if (sortByName === SortbyName.Ascending) {
      setFilteredValues((prevFiltered) => {
        return prevFiltered.sort((a, b) => a.localeCompare(b));
      });
    } else {
      setFilteredValues((prevFiltered) => {
        return prevFiltered.sort((a, b) => b.localeCompare(a));
      });
    }
    setFlag(!flag);
  }, [sortByName]);

  function returnBlockType(value: string) {
    value = value.split("_partial_")[0] || value;
    if (keyword.blocked.find((b) => b === value)) return "Block";
    if (keyword.suspended.find((s) => s === value)) return "Suspended";
    if (keyword.flagged.find((f) => f === value)) return "Flag";

    // Partial values
    if (keyword.partialKeywordsBlocked?.find((b) => b.keyword === value))
      return "Block";
    if (keyword.partialKeywordsSuspended?.find((s) => s.keyword === value))
      return "Suspended";
    if (keyword.partialKeywordsFlagged?.find((f) => f.keyword === value))
      return "Flag";
  }

  // TODO! Implement this properly when the backend is done
  function returnMatchType(value: string) {
    const type = value.split("_partial_")[1];

    return MATCH_MODE_LABEL_MAP[type || "EXACT_MATCH"];
  }

  async function handleDelete(value: string) {
    switch (selected) {
      case BlockingTypes.EMAIL: {
        await removeEmailRestrictions({ emailsBlocked: [value] });
        break;
      }

      case BlockingTypes.PHONE: {
        await removeEmailRestrictions({ phoneNumbersBlocked: [value] });
        break;
      }

      case BlockingTypes.DOMAIN: {
        await removeEmailRestrictions({ espsBlocked: [value] });
        break;
      }

      case BlockingTypes.COUNTRIES: {
        if (user.options.countriesList === "BLACKLIST") {
          await removeEmailRestrictions({
            countriesBlocked: [
              countries.find((a) => a.text === value)?.key || "",
            ],
          });
        } else {
          await removeEmailRestrictions({
            countriesWhitelisted: [
              countries.find((a) => a.text === value)?.key || "",
            ],
          });
        }

        break;
      }

      case BlockingTypes.COUNTRY_CODE: {
        if (user.options.countryCodesList === "BLACKLIST") {
          await removeEmailRestrictions({ countryCodesBlocked: [value] });
        } else {
          await removeEmailRestrictions({ countryCodesWhitelisted: [value] });
        }
        break;
      }

      case BlockingTypes.DEVICE_IP: {
        await removeEmailRestrictions({ deviceIpsBlocked: [value] });
        break;
      }

      case BlockingTypes.DEVICE_ID: {
        await removeEmailRestrictions({ deviceIdsBlocked: [value] });
        break;
      }

      case BlockingTypes.KEYWORD: {
        const [keyword, type] = value.split("_partial_");

        const partialNode: IPartialKeyword = {
          keyword,
          type: type as "CONTAINS" | "ENDS_WITH" | "STARTS_WITH",
        };

        if (type) {
          await removeEmailRestrictions({
            partialKeywordsBlocked: [partialNode],
            partialKeywordsSuspended: [partialNode],
            partialKeywordsFlagged: [partialNode],
          });
        } else {
          await removeEmailRestrictions({
            keywordsBlocked: [keyword],
            keywordsFlagged: [keyword],
            keywordsSuspended: [keyword],
          });
        }

        break;
      }
    }

    const emailRestrictions = await getEmailRestrictions();
    setEmailRestrictions(emailRestrictions);
    setUser((u) => ({ ...u, emailRestrictions }));
    toast("Success", { type: "success" });
  }

  function getSelected(defaultSelected: BlockingTypes, returnType: string) {
    if (returnType === "BLOCKED_VALUE") {
      if (defaultSelected === BlockingTypes.EMAIL) return "blocked emails";
      if (defaultSelected === BlockingTypes.PHONE) return "blocked phones";
      if (defaultSelected === BlockingTypes.KEYWORD) return "blocked keywords";
      if (defaultSelected === BlockingTypes.DOMAIN) return "blocked domains";
      if (defaultSelected === BlockingTypes.COUNTRIES) {
        if (user.options.countriesList === "BLACKLIST") {
          return "blocked countries";
        } else {
          return "allowed countries";
        }
      }
      if (defaultSelected === BlockingTypes.COUNTRY_CODE) {
        if (user.options.countryCodesList === "BLACKLIST") {
          return "blocked country codes";
        } else {
          return "allowed country codes";
        }
      }
      if (defaultSelected === BlockingTypes.DEVICE_IP)
        return "blocked devices IP";
      if (defaultSelected === BlockingTypes.DEVICE_ID)
        return "blocked devices ID";
    } else if (returnType === "BLOCKED_ANY_VALUE") {
      if (defaultSelected === BlockingTypes.EMAIL) return "blocked any emails";
      if (defaultSelected === BlockingTypes.PHONE) return "blocked any phones";
      if (defaultSelected === BlockingTypes.KEYWORD)
        return "blocked any keywords";
      if (defaultSelected === BlockingTypes.DOMAIN)
        return "blocked any domains";
      if (defaultSelected === BlockingTypes.COUNTRIES) {
        if (user.options.countriesList === "BLACKLIST") {
          return "blocked any countries";
        } else {
          return "allowed any countries";
        }
      }
      if (defaultSelected === BlockingTypes.COUNTRY_CODE) {
        if (user.options.countryCodesList === "BLACKLIST") {
          return "blocked any country codes";
        } else {
          return "allowed any country codes";
        }
      }
      if (defaultSelected === BlockingTypes.DEVICE_IP)
        return "blocked any devices IP";
      if (defaultSelected === BlockingTypes.DEVICE_ID)
        return "blocked any devices ID";
    }

    if (defaultSelected === BlockingTypes.EMAIL) return "Block Email";
    if (defaultSelected === BlockingTypes.PHONE) return "Block Phone";
    if (defaultSelected === BlockingTypes.KEYWORD) return "Block Keyword";
    if (defaultSelected === BlockingTypes.DOMAIN) return "Block Domain";
    if (defaultSelected === BlockingTypes.COUNTRIES) {
      if (user.options.countriesList === "BLACKLIST") {
        return "Block Country";
      } else {
        return "Allow Country";
      }
    }
    if (defaultSelected === BlockingTypes.COUNTRY_CODE) {
      if (user.options.countryCodesList === "BLACKLIST") {
        return "Block Country Code";
      } else {
        return "Allow Country Code";
      }
    }
    if (defaultSelected === BlockingTypes.DEVICE_IP) return "Block Device IP";
    if (defaultSelected === BlockingTypes.DEVICE_ID) return "Block Device ID";
  }

  function getBlockCountryTypeSelected(
    defaultSelected: "BLACKLIST" | "WHITELIST"
  ) {
    if (defaultSelected === "WHITELIST") return "allowed";
    if (defaultSelected === "BLACKLIST") return "blocked";
  }

  function getTableHeader() {
    if (filtered && values.length > 1) {
      return "Search Results";
    } else {
      switch (selected) {
        case BlockingTypes.EMAIL:
          return (
            selected === BlockingTypes.EMAIL &&
            (values.length > 1
              ? `${values.length} emails blocked`
              : values.length < 1
              ? "No email blocked"
              : `${values.length} email blocked`)
          );
        case BlockingTypes.PHONE:
          return (
            selected === BlockingTypes.PHONE &&
            (values.length > 1
              ? `${values.length} phones blocked`
              : values.length < 1
              ? "No phone blocked"
              : `${values.length} phone blocked`)
          );
        case BlockingTypes.KEYWORD:
          return (
            selected === BlockingTypes.KEYWORD &&
            (values.length > 1
              ? `${values.length} keywords blocked`
              : values.length < 1
              ? "No keyword blocked"
              : `${values.length} keyword blocked`)
          );
        case BlockingTypes.DOMAIN:
          return (
            selected === BlockingTypes.DOMAIN &&
            (values.length > 1
              ? `${values.length} domains blocked`
              : values.length < 1
              ? "No domain blocked "
              : `${values.length} domain blocked`)
          );
        case BlockingTypes.COUNTRIES:
          return (
            selected === BlockingTypes.COUNTRIES &&
            (values.length > 1
              ? `${values.length} countries ${getBlockCountryTypeSelected(
                  user.options.countriesList
                )}`
              : values.length < 1
              ? `No countries ${getBlockCountryTypeSelected(
                  user.options.countriesList
                )}`
              : `${values.length} country ${getBlockCountryTypeSelected(
                  user.options.countriesList
                )}`)
          );
        case BlockingTypes.COUNTRY_CODE:
          return (
            selected === BlockingTypes.COUNTRY_CODE &&
            (values.length > 1
              ? `${values.length} country codes ${getBlockCountryTypeSelected(
                  user.options.countryCodesList
                )}`
              : values.length < 1
              ? `No country codes ${getBlockCountryTypeSelected(
                  user.options.countryCodesList
                )}`
              : `${values.length} country code ${getBlockCountryTypeSelected(
                  user.options.countryCodesList
                )}`)
          );
        case BlockingTypes.DEVICE_IP:
          return (
            selected === BlockingTypes.DEVICE_IP &&
            (values.length > 1
              ? `${values.length} devices IP blocked`
              : values.length < 1
              ? "No device IP blocked"
              : `${values.length} device IP blocked`)
          );
        case BlockingTypes.DEVICE_ID:
          return (
            selected === BlockingTypes.DEVICE_ID &&
            (values.length > 1
              ? `${values.length} devices ID blocked`
              : values.length < 1
              ? "No device ID blocked"
              : `${values.length} device ID blocked`)
          );
        default:
          break;
      }
    }
  }

  function getEmptyState() {
    return (
      <div className="image-wrapper">
        <img src="/icons/blocking/NoContent.svg" alt="No Content Image" />
        <div className="primary">
          No {getSelected(selected, "BLOCKED_VALUE")} to show
        </div>
        <div className="secondary">
          {values.length > 0
            ? `Sorry, we can't find ${getSelected(
                selected,
                "BLOCKED_ANY_VALUE"
              )} matching your search criteria`
            : `You haven’t ${getSelected(selected, "BLOCKED_ANY_VALUE")} yet.`}
        </div>
      </div>
    );
  }

  function getContent() {
    if (values.length > 0) {
      if (filtered && !filteredValues.length) return getEmptyState();

      return filteredValues.map((v) => {
        return (
          /*  If Blocking type is KEYWORD, we need to include one more column in the tableRows
              So, I'm incrementing grid-template-column
          */
          <TableRowItem key={v} isKeyword={selected === BlockingTypes.KEYWORD}>
            {selected === BlockingTypes.COUNTRIES ? (
              <div
                className="text"
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                <Flag
                  src={`https://flagcdn.com/h24/${
                    countries.find((a) => a.text === v)?.key || ""
                  }.png`}
                />
                <span style={{ marginLeft: "11px" }}>{v}</span>
              </div>
            ) : (
              <div className="text">{v.split("_partial_")[0] || v}</div>
            )}
            {selected === BlockingTypes.KEYWORD && (
              <>
                <div className="text">
                  <BlockTag className={returnBlockType(v)}>
                    {returnBlockType(v)}
                  </BlockTag>
                </div>
                <div className="text">
                  <BlockTag className={returnBlockType(v)}>
                    {returnMatchType(v)}
                  </BlockTag>
                </div>
              </>
            )}

            <div
              className="icon-button"
              onClick={() =>
                modal.pushModal(
                  <Warning
                    icon={"./icons/delete.svg"}
                    title={(() => {
                      if (type === BlockingTypes.COUNTRIES) {
                        if (user.options.countriesList === "WHITELIST") {
                          return `Remove ${getSelected(selected, "")
                            .split("Block ")
                            .join("")}`;
                        }
                      }

                      if (type === BlockingTypes.COUNTRY_CODE) {
                        if (user.options.countryCodesList === "WHITELIST") {
                          return `Remove ${getSelected(selected, "")
                            .split("Block ")
                            .join("")}`;
                        }
                      }

                      return `Unblock ${getSelected(selected, "")
                        .split("Block ")
                        .join("")}`;
                    })()}
                    description={
                      <div style={{ textAlign: "center" }}>
                        Are you sure you want to{" "}
                        {(type === BlockingTypes.COUNTRIES &&
                          user.options.countriesList === "WHITELIST") ||
                        (type === BlockingTypes.COUNTRY_CODE &&
                          user.options.countryCodesList === "WHITELIST")
                          ? "remove "
                          : "unblock "}
                        <b>{v.split("_partial_")[0] || v}</b>?
                      </div>
                    }
                    buttonsName={["No", "Yes"]}
                    onOkClicked={async () => {
                      try {
                        handleDelete(v);
                      } catch (e) {
                        console.log(e);
                      } finally {
                        modal.popModal();
                      }
                    }}
                    onCancelClicked={() => modal.popModal()}
                  />
                )
              }
            >
              <FaTrashAltIcon />
            </div>
          </TableRowItem>
        );
      });
    } else {
      return getEmptyState();
    }
  }

  return (
    <>
      <TableContainer>
        <TableHeader>
          <div className="table-title">{getSelected(selected, "")}</div>

          {(selected === BlockingTypes.COUNTRIES ||
            selected === BlockingTypes.COUNTRY_CODE) && (
            <div className="country-button" onClick={onOpen}>
              What’s the difference between Blacklist & Whitelist?
            </div>
          )}

          <div className="add-input-wrapper">
            {selected === BlockingTypes.EMAIL && <BlockEmail />}
            {selected === BlockingTypes.PHONE && <BlockPhone />}
            {selected === BlockingTypes.KEYWORD && <BlockKeyword />}
            {selected === BlockingTypes.DOMAIN && <BlockDomain />}
            {selected === BlockingTypes.COUNTRIES && (
              <BlockCountry disabled={disableWhitelistBlacklistToggle} />
            )}
            {selected === BlockingTypes.COUNTRY_CODE && (
              <BlockCountryCode disabled={disableWhitelistBlacklistToggle} />
            )}
            {selected === BlockingTypes.DEVICE_IP && <BlockDeviceIP />}
            {selected === BlockingTypes.DEVICE_ID && <BlockDeviceID />}
          </div>

          {values.length > 0 && (
            <div className="label-search-wrapper">
              <div className="label-text">{getTableHeader()}</div>

              <div className="input-wrapper">
                <SearchInput
                  value={filtered}
                  handleChange={(vle) => setFiltered(vle)}
                  placeholder={`Search for ${getSelected(
                    selected,
                    "BLOCKED_VALUE"
                  )}`}
                />
              </div>
            </div>
          )}
        </TableHeader>

        {/* If Blocking type is KEYWORD, we need to include two more columns in the tableHeader
            So, I'm incrementing grid-template-column
        */}
        {values.length > 0 && !!filteredValues.length && (
          <TableCardHeader
            isKeyword={selected === BlockingTypes.KEYWORD}
            key={`${flag}`}
          >
            <TableHeadText>
              <FlexRow
                onClick={() => {
                  setSortByName((s) => {
                    if (s === SortbyName.Ascending)
                      return SortbyName.Descending;
                    return SortbyName.Ascending;
                  });
                }}
              >
                {selected === BlockingTypes.EMAIL && "EMAIL ADDRESS"}
                {selected === BlockingTypes.PHONE && "PHONE"}
                {selected === BlockingTypes.KEYWORD && "KEYWORD"}
                {selected === BlockingTypes.DOMAIN && "DOMAIN"}
                {selected === BlockingTypes.COUNTRIES && "COUNTRIES"}
                {selected === BlockingTypes.COUNTRY_CODE && "COUNTRY CODE"}
                {selected === BlockingTypes.DEVICE_IP && "DEVICE IP"}
                {selected === BlockingTypes.DEVICE_ID && "DEVICE ID"}
                <Icon src={"./icons/contacts/arrow.svg"} />
              </FlexRow>
            </TableHeadText>

            {selected === BlockingTypes.KEYWORD && (
              <>
                <TableHeadText>
                  <FlexRow>
                    TYPE
                    <Icon src={"./icons/contacts/arrow.svg"} />
                  </FlexRow>
                </TableHeadText>

                <TableHeadText>
                  <FlexRow>
                    MODE
                    <Icon src={"./icons/contacts/arrow.svg"} />
                  </FlexRow>
                </TableHeadText>
              </>
            )}

            <TableActions></TableActions>
          </TableCardHeader>
        )}

        <TableRowWrapper>{getContent()}</TableRowWrapper>
      </TableContainer>
      <BlackWhiteListModal isOpen={isOpen} onClose={onClose} />
    </>
  );
}
